import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { downloadFile } from '../utils/downloadFile';
import { FORM_IDS, getFormAssetPath } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

interface FormValues {
  name: string;
  email: string;
  phone: string;
  company: string;
  sessionId: string;
  consent: boolean;
  website: string;
}

const WEBINAR_SESSIONS = [
  {
    value: '2026-11-17',
    label: '17.11.2026, 10:00 - 11:00 Uhr',
    registrationClosesAt: '2026-11-17T11:00:00+01:00',
    fileName: '2026-11-17_Webinar.ics',
    downloadName: 'AgentPulse-2026-11-17.ics',
  },
  {
    value: '2026-11-19',
    label: '19.11.2026, 15:00 - 16:00 Uhr',
    registrationClosesAt: '2026-11-19T16:00:00+01:00',
    fileName: '2026-11-19_Webinar.ics',
    downloadName: 'AgentPulse-2026-11-19.ics',
  },
];

const validationSchema = Yup.object({
  name: Yup.string().trim().max(255, 'Name darf maximal 255 Zeichen enthalten').required('Name ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail ist erforderlich'),
  phone: Yup.string().max(255, 'Telefonnummer darf maximal 255 Zeichen enthalten'),
  company: Yup.string().max(255, 'Unternehmen darf maximal 255 Zeichen enthalten'),
  sessionId: Yup.string().oneOf(WEBINAR_SESSIONS.map((session) => session.value), 'Bitte wähle einen gültigen Webinar-Termin aus')
    .required('Bitte wähle einen Webinar-Termin aus'),
  consent: Yup.boolean()
    .required('Bitte stimme den Datenschutzbestimmungen zu')
    .oneOf([true], 'Du musst den Datenschutzbestimmungen zustimmen, um Dich für das Webinar anzumelden'),
});

const initialValues: FormValues = {
  name: '',
  email: '',
  phone: '',
  company: '',
  sessionId: '',
  consent: false,
  website: '',
};

const API_ENDPOINT = '/api/submit-webinar';

export const AgentPulse = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [selectedSession, setSelectedSession] = useState('');
  const [now, setNow] = useState(Date.now());
  const formMountedAtRef = useRef(Date.now());
  const headerImage = useHeaderImage(FORM_IDS.AGENT_PULSE);
  const registrationIsOpen = WEBINAR_SESSIONS.some((session) => now < new Date(session.registrationClosesAt).getTime());

  useEffect(() => {
    const nextBoundary = [
      ...WEBINAR_SESSIONS.map((session) => new Date(session.registrationClosesAt).getTime()),
    ].filter((boundary) => boundary > now).sort((first, second) => first - second)[0];
    if (!nextBoundary) return;

    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.min(nextBoundary - now + 1000, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [now]);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    const session = WEBINAR_SESSIONS.find((option) => option.value === values.sessionId);
    const submissionTime = Date.now();
    if (!session || submissionTime >= new Date(session.registrationClosesAt).getTime()) {
      setNow(submissionTime);
      setErrorMessage('Die Anmeldung für diesen Webinar-Termin ist derzeit nicht möglich.');
      setShowErrorSnackbar(true);
      setSubmitting(false);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          name: values.name,
          mail: values.email,
          phone: values.phone,
          company: values.company,
          sessionId: values.sessionId,
          consent: values.consent,
          website: values.website,
          elapsedMs: Date.now() - formMountedAtRef.current,
        },
        debugFieldValue: values.name,
      });

      if (result.success) {
        setSelectedSession(values.sessionId);
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (error) {
      console.error('Fehler beim Absenden des Webinarformulars:', error);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleDownload = () => {
    const session = WEBINAR_SESSIONS.find((option) => option.value === selectedSession);
    if (!session) return;

    downloadFile({
      fileUrl: getFormAssetPath(FORM_IDS.AGENT_PULSE, session.fileName),
      fileName: session.downloadName,
      mimeType: 'text/calendar',
      redirectUrl: 'https://www.lansco.de/',
    });
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="KI-Agenten sicher im Griff – AgentPulse in Kooperation mit AvePoint" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {isSubmitted ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank für Deine Anmeldung!
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Deine Anmeldung wurde erfolgreich empfangen. Lade Dir jetzt den Kalendertermin herunter, damit Du das Webinar nicht verpasst.
            </Typography>
            <LanscoButton onClick={handleDownload} sx={{ minWidth: 220 }}>
              Kalendertermin herunterladen
            </LanscoButton>
          </Box>
        ) : (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, color: 'primary.main' }}>
              KI-Agenten sicher im Griff!
            </Typography>
            <Typography variant="h5" component="p" align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              So behältst Du den Überblick über Datenzugriffe, Kosten und Risiken
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
              Microsoft Copilot, Copilot Studio und KI-Agenten halten zunehmend Einzug in den Unternehmensalltag. Sie automatisieren Prozesse, unterstützen Mitarbeitende und greifen dabei auf Informationen aus unterschiedlichsten Quellen zu.
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
              Doch mit der wachsenden Nutzung entstehen neue Fragen:
            </Typography>
            <Typography variant="body1" component="ul" sx={{ mb: 2, pl: 2, lineHeight: 1.6, '& li': { mb: 1 } }}>
              <li>Welche KI-Agenten sind überhaupt im Einsatz?</li>
              <li>Wer hat sie erstellt und wer ist dafür verantwortlich?</li>
              <li>Auf welche Daten greifen sie zu?</li>
              <li>Wo entstehen Sicherheits- und Compliance-Risiken?</li>
              <li>Wie lassen sich Nutzung, Kosten und Governance kontrollieren?</li>
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
              In unserem gemeinsamen Webinar mit AvePoint zeigen wir Dir, wie Unternehmen Transparenz über ihre KI-Agenten schaffen und gleichzeitig die notwendige Kontrolle behalten.
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
              Anhand einer praxisnahen Demonstration erfährst Du, wie AgentPulse dabei unterstützt, Agenten sichtbar zu machen und wie sich mit Policies &amp; Insights klare Verantwortlichkeiten sowie automatisierte Governance-Richtlinien etablieren lassen.
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
              Melde Dich jetzt kostenlos an und erfahre, wie Du KI-Agenten sicher und kontrolliert in Deinem Unternehmen betreiben kannst.
            </Typography>
            <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
              Das erwartet Dich im Webinar
            </Typography>
            <Typography variant="body1" component="ul" sx={{ mb: 2, pl: 2, lineHeight: 1.6, '& li': { mb: 1 } }}>
              <li>Wie Du Dir einen zentralen Überblick über vorhandene KI-Agenten verschaffen kannst</li>
              <li>Wie sich Nutzung, Kosten und potenzielle Risiken sichtbar machen lassen</li>
              <li>Wie Du nachvollziehen kannst, wer Agenten erstellt hat und auf welche Daten sie zugreifen</li>
              <li>Wie sich Oversharing und Zugriffe auf kritische Daten erkennen lassen</li>
              <li>Wie Du Verantwortlichkeiten und den Lebenszyklus von KI-Agenten steuerst</li>
              <li>Wie sich Governance-Richtlinien automatisiert umsetzen lassen</li>
              <li>Wie Du Microsoft Copilot, Copilot Studio und weitere Agent-Plattformen sicher betreiben kannst</li>
            </Typography>
            {!registrationIsOpen ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="h6" component="h2" sx={{ color: 'primary.main', mb: 2 }}>
                  Anmeldung geschlossen
                </Typography>
                <Typography variant="body1">
                  Die Anmeldung für das Webinar „KI-Agenten sicher im Griff“ ist leider nicht mehr möglich.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h6" component="h2" sx={{ mb: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  Jetzt anmelden!
                </Typography>
                <Typography variant="body1" component="p" sx={{ mb: 2, lineHeight: 1.6 }}>
                  Melde Dich einfach mit Deinen Kontaktdaten an und wähle Deinen bevorzugten Webinar-Termin aus. Nach erfolgreicher Anmeldung kannst Du Dir den Termin herunterladen und in Deinem Kalender speichern.
                </Typography>
                {isLoading && (
                  <FormLoadingOverlay>
                    <CircularProgress size={60} sx={{ color: 'primary.main' }} />
                  </FormLoadingOverlay>
                )}
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                  validateOnMount
                  initialTouched={{ consent: true }}
                >
                  {({ errors, touched, isValid, dirty, values, handleChange }) => (
                    <Form>
                      <Box
                        aria-hidden="true"
                        sx={{ position: 'absolute', left: '-5000px', width: 1, height: 1, overflow: 'hidden' }}
                      >
                        <label htmlFor="website">Dieses Feld bitte leer lassen</label>
                        <input
                          id="website"
                          type="text"
                          name="website"
                          tabIndex={-1}
                          autoComplete="off"
                          value={values.website}
                          onChange={handleChange}
                        />
                      </Box>
                      <Field
                        as={TextField}
                        fullWidth
                        margin="normal"
                        name="name"
                        label="Name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        sx={{ mb: 2 }}
                        disabled={isLoading}
                      />
                      <Field
                        as={TextField}
                        fullWidth
                        margin="normal"
                        name="email"
                        label="E-Mail"
                        type="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        sx={{ mb: 2 }}
                        disabled={isLoading}
                      />
                      <Field
                        as={TextField}
                        fullWidth
                        margin="normal"
                        name="phone"
                        label="Telefonnummer"
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        sx={{ mb: 2 }}
                        disabled={isLoading}
                      />
                      <Field
                        as={TextField}
                        fullWidth
                        margin="normal"
                        name="company"
                        label="Unternehmen"
                        error={touched.company && Boolean(errors.company)}
                        helperText={touched.company && errors.company}
                        sx={{ mb: 2 }}
                        disabled={isLoading}
                      />
                      <Field
                        as={TextField}
                        select
                        fullWidth
                        margin="normal"
                        name="sessionId"
                        label="Webinar-Termin auswählen"
                        error={touched.sessionId && Boolean(errors.sessionId)}
                        helperText={touched.sessionId && errors.sessionId}
                        sx={{ mb: 2 }}
                        disabled={isLoading}
                      >
                        {WEBINAR_SESSIONS.map((session) => (
                          <MenuItem
                            key={session.value}
                            value={session.value}
                            disabled={now >= new Date(session.registrationClosesAt).getTime()}
                          >
                            {session.label}
                          </MenuItem>
                        ))}
                      </Field>
                      <PrivacyConsentSection
                        label="Ja, ich stimme zu und möchte mich für das Webinar anmelden"
                        isLoading={isLoading}
                      />
                      {errorMessage && (
                        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                          {errorMessage}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <LanscoButton
                          type="submit"
                          disabled={!(isValid && dirty) || isLoading
                            || !WEBINAR_SESSIONS.some((session) => session.value === values.sessionId
                              && now < new Date(session.registrationClosesAt).getTime())}
                          sx={{ minWidth: 180 }}
                        >
                          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Für Webinar anmelden'}
                        </LanscoButton>
                      </Box>
                    </Form>
                  )}
                </Formik>
              </>
            )}
          </>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};
